import ExcelJS from "exceljs";
import moment from "moment";
import { sortAndGroup } from "./schoolProgramAttendanceSort";

const HEADER_FILL = "FFDCE6F1"; // light blue header (matches on-screen table)
const HEADER_FONT = "FF1F3864"; // dark navy header text
const BORDER_COLOR = "FF000000"; // thin black border on every cell
const TEXT_COLOR = "FF333333";
const FONT_NAME = "Calibri";

// Day name -> weekday number (moment: Sunday = 0 ... Saturday = 6).
const WEEKDAY_NUMBER = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

// Static columns (1-6). Widths/alignment match the reference sheet.
const STATIC_COLUMNS = [
  { header: "School", field: "school_name", width: 17.33, align: "left" },
  { header: "Course", field: "course_abbr", width: 17.33, align: "left" },
  { header: "Student ID", field: "student_id", width: 9.33, align: "center" },
  { header: "Student Name", field: "student_name", width: 15.83, align: "left" },
  { header: "Parent Name", field: "parent_name", width: 19.66, align: "left" },
  { header: "Phone Number", field: "phone", width: 12.83, align: "center" },
];

const DATE_COL_WIDTH = 6.5;

const allBorders = {
  top: { style: "thin", color: { argb: BORDER_COLOR } },
  left: { style: "thin", color: { argb: BORDER_COLOR } },
  bottom: { style: "thin", color: { argb: BORDER_COLOR } },
  right: { style: "thin", color: { argb: BORDER_COLOR } },
};

function styleHeaderCell(cell) {
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
  cell.font = { name: FONT_NAME, bold: true, size: 11, color: { argb: HEADER_FONT } };
  cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  cell.border = allBorders;
}

/**
 * Returns the list of real class dates (as moment objects) for a program: every
 * date matching `dayName` (e.g. "Tuesday") between startdate and enddate
 * inclusive.
 */
export function computeClassDates(startdate, enddate, dayName) {
  const target = WEEKDAY_NUMBER[(dayName || "").trim().toLowerCase()];
  if (target === undefined || !startdate || !enddate) return [];

  const start = moment(startdate, "YYYY-MM-DD");
  const end = moment(enddate, "YYYY-MM-DD");
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) return [];

  const dates = [];
  const cursor = start.clone();
  // Advance to the first occurrence of the target weekday.
  while (cursor.day() !== target && cursor.isSameOrBefore(end, "day")) {
    cursor.add(1, "day");
  }
  while (cursor.isSameOrBefore(end, "day")) {
    dates.push(cursor.clone());
    cursor.add(7, "days");
  }
  return dates;
}

/**
 * Sorted union (de-duplicated) of class dates across all rows being exported,
 * so mixed programs are all covered. Returns an array of moment objects.
 */
function buildDateColumns(rows) {
  const seen = new Map(); // YYYY-MM-DD -> moment
  rows.forEach((row) => {
    computeClassDates(row.startdate, row.enddate, row.day).forEach((d) => {
      const key = d.format("YYYY-MM-DD");
      if (!seen.has(key)) seen.set(key, d);
    });
  });
  return [...seen.keys()]
    .sort()
    .map((key) => seen.get(key));
}

export const generateSchoolProgramAttendanceWorkbook = async (reports) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("School Program Attendance", {
    views: [{ state: "frozen", ySplit: 2 }], // keep the header visible while scrolling
  });

  const dateColumns = buildDateColumns(reports || []);
  const totalCols = STATIC_COLUMNS.length + dateColumns.length * 2;

  // Column widths
  STATIC_COLUMNS.forEach((col, i) => {
    worksheet.getColumn(i + 1).width = col.width;
  });
  for (let c = STATIC_COLUMNS.length + 1; c <= totalCols; c++) {
    worksheet.getColumn(c).width = DATE_COL_WIDTH;
  }

  // ---- Header (rows 1-2) ----
  STATIC_COLUMNS.forEach((col, i) => {
    const colIdx = i + 1;
    worksheet.mergeCells(1, colIdx, 2, colIdx);
    worksheet.getCell(1, colIdx).value = col.header;
  });

  dateColumns.forEach((date, i) => {
    const inCol = STATIC_COLUMNS.length + 1 + i * 2;
    const outCol = inCol + 1;
    worksheet.mergeCells(1, inCol, 1, outCol);
    worksheet.getCell(1, inCol).value = date.format("MMM D");
    worksheet.getCell(2, inCol).value = "In";
    worksheet.getCell(2, outCol).value = "Out";
  });

  // Style both header rows across all columns
  for (let r = 1; r <= 2; r++) {
    for (let c = 1; c <= totalCols; c++) {
      styleHeaderCell(worksheet.getCell(r, c));
    }
  }
  worksheet.getRow(1).height = 24;
  worksheet.getRow(2).height = 18;

  // ---- Data rows ----
  const rows = sortAndGroup(reports);
  let excelRow = 3;

  rows.forEach((row) => {
    if (row.__groupStart) {
      // Light-blue band (header style) across all columns to break school/course groups.
      for (let c = 1; c <= totalCols; c++) {
        styleHeaderCell(worksheet.getCell(excelRow, c));
      }
      excelRow += 1;
    }

    const wsRow = worksheet.getRow(excelRow);
    wsRow.height = 20;

    STATIC_COLUMNS.forEach((col, i) => {
      const cell = wsRow.getCell(i + 1);
      cell.value = row[col.field] ?? "";
      cell.alignment = { vertical: "middle", horizontal: col.align };
      cell.font = { name: FONT_NAME, size: 11, color: { argb: TEXT_COLOR } };
    });

    // In/Out date cells are left empty (writable template); borders only.
    for (let c = 1; c <= totalCols; c++) {
      wsRow.getCell(c).border = allBorders;
    }

    excelRow += 1;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export const downloadSchoolProgramAttendanceSheet = async (reports, branchName) => {
  const blob = await generateSchoolProgramAttendanceWorkbook(reports);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute(
    "download",
    `${branchName} - School Program Attendance Sheet.xlsx`
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
